import { PaymentElement } from "@stripe/react-stripe-js";
import {
    Appearance,
    PaymentMethodResult,
    Stripe,
    StripeElements,
    StripePaymentElementChangeEvent
} from "@stripe/stripe-js";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import type StripeTypes from "stripe";

import { workspaces as workspacesApi } from "apis/callables";
import stripeProducts from "common/stripeProducts";
import { Button } from "js/Components/Button";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { handleStripeErrors } from "js/core/services/stripe";
import { Gap20 } from "js/react/components/Gap";
import { MinimalBillingAddressForm } from "js/react/views/UserOptions/Billing/MinimalBillingAddressForm";
import withElements from "js/react/views/UserOptions/Billing/withElements";
import withStripe from "js/react/views/UserOptions/Billing/withStripe";
import PromoField from "js/react/views/UserOptions/components/PromoField";
import { IPromoDescription } from "common/interfaces";

import { BodySmall } from "./FormStyling";

import "css/billing.scss";

const logger = getLogger(LogGroup.BILLING);

const Container = styled.div`
    @media only screen and (min-height: 700px) {
        align-items: center;
    }
    width: 100%;
    position: relative;
`;

const Title = styled(BodySmall)`
    margin-bottom: 12px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const AddressContainer = styled.div`
    margin-top: 5px;
    margin-bottom: 12px;
    gap: 12px;
`;

const PaymentElementContainer = styled.div`
    width: 100%;
`;

const CancelButton = styled(Button)`
    background: transparent;
    color: #11a9e2;
`;

const PaymentError = styled.div`
    color: white;
    background: orangered;
    letter-spacing: 0px;
    padding: 8px 15px;
    margin-top: 20px;
    font-size: 15px;
    font-weight: 600;
`;

const SubmitButton = styled(Button) <{ fullWidth?: boolean }>`
    width: ${props => props.fullWidth ? "100%" : "200px"};
`;

const appearance: Appearance = {
    labels: "floating",
    variables: {
        spacingGridRow: "12px",
        spacingGridColumn: "12px",
        spacingUnit: "4px",
        spacingTab: "4px",
        colorDanger: "#b40000",
        colorPrimary: "#11a9e2",
        colorPrimaryText: "#11a9e2",
        colorText: "#222",
        colorTextSecondary: "#666",
        colorTextPlaceholder: "#999",
        fontFamily: "Source Sans Pro",
        fontWeightNormal: "400",
        fontWeightBold: "600",
        fontSizeBase: "16px",
        fontSizeSm: "14px",
        borderRadius: "0"
    },
    rules: {
        ".Input": {
            fontSize: "var(--fontSizeBase)",
            lineHeight: "1.15",
            letterSpacing: "-0.01em",
            padding: "3px 0 7px",
            boxShadow: "none",
            border: `none`,
            color: "var(--colorTextSecondary)",
            borderBottom: "1px solid #d0d0d0"
        },
        ".Input:focus": {
            boxShadow: "none",
            outline: "none",
            borderBottom: "1px solid var(--colorPrimary)",
        },
        ".Input--invalid": {
            borderBottom: "1px solid var(--colorDanger)",
            color: "var(--colorText)",
            boxShadow: "none",
        },
        ".Input:hover": {
            boxShadow: "none",
            borderBottom: "1px solid #333",
        },
        ".Label": {
            fontSize: "var(--fontSizeBase)",
            lineHeight: "18px",
            letterSpacing: "-0.01em",
            color: "var(--colorTextSecondary)",
            margin: "4px 0 7px"
        },
        ".Label--floating": {
            fontSize: "var(--fontSizeSm)",
            lineHeight: "16px",
            opacity: "1",
            margin: "-2px 0 0",
            padding: "0",
            transition: "all 300ms cubic-bezier(0.5, 0, 0.2, 1)",
            color: "var(--colorTextSecondary)",
        },
        ".Error": {
            fontSize: "12px",
            lineHeight: "15px",
            color: "var(--colorDanger)"
        },
        ".TermsText": {
            fontSize: "14px",
            lineHeight: "1.6",
            letterSpacing: "-0.02em",
            color: "var(--colorTextSecondary)",
            marginTop: "0px"
        }
    }
};

export interface PaymentFormSubmitProps {
    paymentMethod: StripeTypes.PaymentMethod,
    promo: IPromoDescription,
    taxAmount: number,
    billingAddress: StripeTypes.Address,
    taxId?: Pick<StripeTypes.TaxId, "type" | "value">;
}

interface PaymentFormProps {
    stripe: Stripe,
    elements: StripeElements,

    priceId: string;
    isFirstTimeTransaction?: boolean;

    onBeforeSubmit?: (submitProps: Omit<PaymentFormSubmitProps, "paymentMethod">) => boolean;
    onSubmit: (submitProps: PaymentFormSubmitProps) => void;
    onPromoChanged?: (promo: IPromoDescription) => void;
    onTaxCalculated?: (taxAmount: number) => void;
    taxIdError?: string;

    onCancel?: () => void;
    cancelLabel?: string;

    showTaxIdForm?: boolean;
    title?: string;
    submitLabel?: string;
    disabled?: boolean;
    className?: string;
}

const PaymentForm = ({
    stripe,
    elements,
    priceId,
    isFirstTimeTransaction,
    onBeforeSubmit,
    onSubmit,
    onPromoChanged,
    onTaxCalculated,
    taxIdError,
    onCancel,
    cancelLabel = "Cancel",
    showTaxIdForm = true,
    title = "Payment details",
    submitLabel,
    disabled,
    className
}: PaymentFormProps) => {
    if (!priceId) {
        throw new Error("priceId is required");
    }

    const [promo, setPromo] = useState<IPromoDescription | null>(null);
    const [billingAddress, setBillingAddress] = useState<StripeTypes.Address>({
        country: "",
        postal_code: "",
        city: "",
        line1: "",
        line2: "",
        state: ""
    });
    const [isBillingAddressValid, setIsBillingAddressValid] = useState(false);
    const [paymentMethodCreationError, setPaymentMethodCreationError] = useState<string | null>(null);
    const [taxIdType, setTaxIdType] = useState<string | null>(null);
    const [taxId, setTaxId] = useState("");
    const [isTaxIdValid, setIsTaxIdValid] = useState(true);
    const [taxAmount, setTaxAmount] = useState(0);
    const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false);

    const getProduct = () => {
        let productId: string, productName: string, productPrice: number, billingInterval: "month" | "year";
        for (const product of Object.values(stripeProducts)) {
            for (const plan of product.plans) {
                if ("planId" in plan && plan.planId === priceId) {
                    productId = product.productId;
                    productName = product.productName;
                    productPrice = plan.amount;
                    billingInterval = plan.interval;
                }
            }
        }

        if (!productId) {
            throw new Error(`Price ${priceId} not found`);
        }

        return {
            productId,
            productName,
            productPrice,
            billingInterval
        };
    };

    const calculateTaxes = async () => {
        const { productPrice, productId } = getProduct();

        try {
            const { taxAmount: calculatedTaxAmount } = await workspacesApi.calculateTaxAmount({
                amount: productPrice,
                stripeProductId: productId,
                countryCode: billingAddress.country,
                postalCode: billingAddress.postal_code,
                taxId
            });
            setTaxAmount(calculatedTaxAmount);
            onTaxCalculated?.(calculatedTaxAmount);
        } catch (err) {
            logger.error(err, "[PaymentForm] failed to calculate tax");
            setTaxAmount(0);
            onTaxCalculated?.(0);
        }
    };

    useEffect(() => {
        if (isBillingAddressValid && isTaxIdValid && billingAddress.country) {
            calculateTaxes();
        }
    }, [billingAddress.country, billingAddress.postal_code, priceId, taxId, taxIdType, isBillingAddressValid, isTaxIdValid]);

    const handleSubmit = async () => {
        const submitProps: Omit<PaymentFormSubmitProps, "paymentMethod"> = {
            promo,
            billingAddress,
            taxAmount,
            taxId: (isTaxIdValid && taxId) ? {
                type: taxIdType as StripeTypes.TaxId.Type,
                value: taxId
            } : undefined
        };

        if (onBeforeSubmit && !onBeforeSubmit(submitProps)) {
            return;
        }

        try {
            setIsCreatingPaymentMethod(true);

            const { error } = await elements.submit();
            if (error) {
                setPaymentMethodCreationError(error.message);
                return;
            }

            const { paymentMethod } = await handleStripeErrors<PaymentMethodResult>(() => stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        address: {
                            country: billingAddress.country,
                            postal_code: billingAddress.postal_code
                        }
                    }
                }
            }));

            onSubmit({
                ...submitProps,
                paymentMethod: paymentMethod as unknown as StripeTypes.PaymentMethod
            });
        } catch (err) {
            logger.error(err, "[PaymentForm] failed to create payment method");

            setPaymentMethodCreationError(err.message);
        } finally {
            setIsCreatingPaymentMethod(false);
        }
    };

    // NOTE: we're not catching here as the caller does the catching
    const handleFetchPromo = async (code: string) => {
        const { productPrice, productName } = getProduct();

        const promo = await Api.promos.get({ code });

        // check restrictions
        if (promo.appliesTo && !promo.appliesTo.includes(productName.toLowerCase())) {
            throw new Error(`This coupon can't be applied to a ${productName} plan`);
        }

        if (promo.minimumAmount && productPrice < promo.minimumAmount) {
            throw new Error(`This promo can't be applied to bills of less than $${promo.minimumAmount / 100}`);
        }

        if (promo.firstTimeTransaction && !isFirstTimeTransaction) {
            throw new Error("This promo is only for new users");
        }

        if (promo.error) {
            throw new Error(promo.error);
        }

        setPromo(promo);
        onPromoChanged?.(promo);

        return promo;
    };

    const handleClearPromo = () => {
        setPromo(null);
        onPromoChanged?.(null);
    };

    const handleBillingAddressFormChange = (change: {
        address: { country: string, postal_code: string },
        isAddressValid: boolean,
        taxId: string,
        taxIdType: string | null,
        isTaxIdValid: boolean
    }) => {
        setBillingAddress({
            ...billingAddress,
            ...change.address
        });
        setIsBillingAddressValid(change.isAddressValid);
        setTaxId(change.taxId);
        setTaxIdType(change.taxIdType);
        setIsTaxIdValid(change.isTaxIdValid);
    };

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
        setPaymentMethodCreationError(null);
    };

    return (
        <Container className={className}>
            {title &&
                <Title className="payment-form-title">
                    {title}
                </Title>
            }
            <PaymentElementContainer>
                <PaymentElement
                    onChange={handlePaymentElementChange}
                    options={{
                        fields: {
                            billingDetails: {
                                address: {
                                    country: "never",
                                    postalCode: "never"
                                }
                            }
                        }
                    }}
                />
                {paymentMethodCreationError &&
                    <PaymentError>{paymentMethodCreationError}</PaymentError>
                }
            </PaymentElementContainer>
            <Gap20 />
            <AddressContainer>
                <MinimalBillingAddressForm
                    taxIdError={taxIdError}
                    initialAddress={billingAddress}
                    initialTaxId={taxId}
                    onChange={handleBillingAddressFormChange}
                    disabled={disabled || isCreatingPaymentMethod}
                    showTaxIdForm={showTaxIdForm}
                />
            </AddressContainer>
            <PromoField
                fetchPromo={handleFetchPromo}
                fetchKey={priceId}
                onClear={handleClearPromo}
            />
            <ButtonsContainer>
                <SubmitButton
                    className="payment-form-submit-button"
                    data-test-id="payment-form-submit-button"
                    blue
                    large
                    fullWidth={!onCancel}
                    disabled={disabled || !isBillingAddressValid || !isTaxIdValid || isCreatingPaymentMethod}
                    onClick={handleSubmit}
                >
                    {submitLabel}
                </SubmitButton>
                {onCancel &&
                    <CancelButton
                        className="payment-form-cancel-button"
                        data-test-id="payment-form-cancel-button"
                        large
                        disabled={disabled || isCreatingPaymentMethod}
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </CancelButton>
                }
            </ButtonsContainer>
        </Container>
    );
};

export default withStripe(withElements(PaymentForm, { appearance }));
